/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ContainerCleaningPayload, PAContainerCleaning } from "../mediaCleaning.types";
import { postAutoclaveServices } from "../../../../../service/postAutoclaveServices";

interface Props {
	passed: number;
	failed: number;
	toggleRecleanModel: () => void;
	showToast: (message: string, toastType: "success" | "error") => void;
	containers: PAContainerCleaning[];
	cleaningAgentId: string;
	cleaningMethod: string;
	handleSuccess: () => void;
	mediaBatchId: string;
}

const PAReCleaningConfirmationModal: React.FC<Props> = ({ passed, failed, toggleRecleanModel, showToast, containers, cleaningAgentId, cleaningMethod, handleSuccess, mediaBatchId }) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);

	// const payload: ContainerCleaningPayload = {
	// 	media_batch_id: mediaBatchId,
	// 	cleaning_agent_id: cleaningAgentId,
	// 	cleaning_method: cleaningMethod,
	// 	containers: containers
	// 		.filter((cont) => cont.visual_cleanliness === "Fail")
	// 		.map((cont) => ({
	// 			container_id: Number(cont.container_id),
	// 			visual_cleanliness: cont.visual_cleanliness,
	// 			remarks: cont.remarks ?? "",
	// 		})),
	// };
	const payload: ContainerCleaningPayload = {
		media_batch_id: mediaBatchId,
		cleaning_agent_id: cleaningAgentId,
		cleaning_method: cleaningMethod,
		containers: containers.map((cont) => ({
			container_id: Number(cont.container_id),
			visual_cleanliness: cont.visual_cleanliness,
			remarks: cont.remarks ?? "",
		})),
	};

	const handlePost = async () => {
		if (isLoading) return;
		setIsLoading(true);
		try {
			const response = await postAutoclaveServices.postContainerCleaning(payload);
			if (response.statusCode !== 200) {
				showToast(response.message || t("postAutoclaveProcess.mediaCleaning.reClean.errorDefault"), "error");
			} else {
				showToast(t("postAutoclaveProcess.mediaCleaning.reClean.successMessage"), "success");
				handleSuccess();
			}
		} catch (error: any) {
			showToast(error?.response?.data?.message || t("postAutoclaveProcess.mediaCleaning.reClean.unexpectedError"), "error");
		} finally {
			setIsLoading(false);
			toggleRecleanModel();
		}
	};

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 animate-in fade-in duration-300">
			<div className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-300">
				<div className="p-12 text-center">
					<div className="w-24 h-24 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-8 border border-orange-100">
						<span className="material-symbols-outlined text-orange-500 text-5xl">refresh</span>
					</div>
					<h3 className="text-2xl font-black text-[#1E293B] mb-4 tracking-tight">
						{t("postAutoclaveProcess.mediaCleaning.reClean.title")}
					</h3>
					<div className="bg-slate-50 rounded-3xl p-8 mb-8 text-left space-y-4">
						<div className="flex items-start gap-4">
							<div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
								<span className="material-symbols-outlined text-red-600">error</span>
							</div>
							<p className="text-sm font-bold text-slate-600 leading-relaxed">
								<span className="text-red-600">
									{t("postAutoclaveProcess.mediaCleaning.reClean.failedPlates", { count: failed })}
								</span>
							</p>
						</div>
						<p className="text-xs text-slate-400 font-medium leading-relaxed pl-14">
							{t("postAutoclaveProcess.mediaCleaning.reClean.auditNote")}
						</p>
						<p className="text-sm font-black text-[#1E293B] pl-14">
							{t("postAutoclaveProcess.mediaCleaning.reClean.confirmPrompt")}
						</p>
					</div>
					<div className="flex gap-4">
						<button
							onClick={() => toggleRecleanModel()}
							disabled={isLoading}
							className="flex-1 px-8 py-4 bg-white border border-slate-200 rounded-2xl text-[11px] font-black text-slate-500 uppercase tracking-widest hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{t("postAutoclaveProcess.mediaCleaning.reClean.cancel")}
						</button>
						<button
							onClick={handlePost}
							disabled={isLoading}
							className="flex-1 px-8 py-4 bg-orange-500 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-orange-200 hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoading
								? t("postAutoclaveProcess.mediaCleaning.reClean.submitting")
								: t("postAutoclaveProcess.mediaCleaning.reClean.submit")}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PAReCleaningConfirmationModal;
